import { NgClass } from '@angular/common';
import {
  Component,
  EventEmitter,
  forwardRef,
  Injector,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl, ValidatorFn } from '@angular/forms';
import { UnknownVoidFunction } from '../../types/unknown-void-function.type';
import { TranslocoModule } from '@ngneat/transloco';

@Component({
  selector: 'app-custom-input',
  standalone: true,
  imports: [NgClass, TranslocoModule],
  templateUrl: './custom-input.component.html',
  styleUrl: './custom-input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomInputComponent),
      multi: true,
    },
  ],
})
export class CustomInputComponent implements ControlValueAccessor, OnInit {
  @Input()
  public label: string;

  @Input()
  public readOnly: boolean;

  @Input()
  public placeholder: string = '';

  @Input()
  public iconUrl: string = '';

  @Output()
  public iconClick = new EventEmitter<void>();

  @Output()
  public inputClick = new EventEmitter<Event>();

  @Output()
  public inputFocus = new EventEmitter<void>();

  @Output()
  public inputBlur = new EventEmitter<void>();

  @Input()
  public required: boolean;

  @Input()
  public secure: boolean;

  @Input()
  public type: string = 'text';

  @Input()
  public disabled: boolean;

  @Input()
  public validators: ValidatorFn[] = [];

  @Output()
  public disabledChange = new EventEmitter<boolean>();

  @Input()
  public hasInputError = false;

  public didBlur: boolean;

  public disableSecurity: boolean;

  public value: string = '';

  @Input()
  public infoIconDescription: string = '';

  // eslint-disable-next-line class-methods-use-this
  public onChange: UnknownVoidFunction = () => {};

  // eslint-disable-next-line class-methods-use-this
  public onTouched: UnknownVoidFunction = () => {};

  public ngControl: NgControl;

  constructor(private readonly injector: Injector) {}

  ngOnInit() {
    const control = this.injector.get(NgControl, null);
    if (control) {
      this.ngControl = control;
      control.valueAccessor = this;

      const formControl = this.ngControl.control;

      if (formControl) {
        formControl.addValidators(this.validators);
        formControl.updateValueAndValidity();
      }
    }
  }

  get hasError(): boolean {
    return !!this.ngControl?.control?.errors || this.hasInputError;
  }

  writeValue(value: string): void {
    this.value = value;
  }

  registerOnChange(fn: UnknownVoidFunction): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: UnknownVoidFunction): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.disabledChange.next(this.disabled);
  }

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.value = input.value;
    this.onChange(this.value);
  }
}
